import { createChildLogger } from "@retreat/shared";
import { TIMING } from "../constants";

/**
 * AudioContextWatcher
 *
 * iOS and some Android devices aggressively suspend Web Audio contexts when
 * the app is backgrounded or the screen is locked. If suspended, bells will
 * not play.
 *
 * Polls the AudioContext returned by getContext() and resumes it if the
 * retreat is running. Accepts a plain callback — no Tone.js dependency.
 */
export class AudioContextWatcher {
  private log = createChildLogger("AudioContextWatcher");
  private isWatching = false;
  private intervalId: ReturnType<typeof setInterval> | null = null;

  /**
   * @param getContext     Returns the AudioContext to watch (or null if not yet unlocked).
   * @param shouldKeepAlive Returns true when audio MUST be running (retreat is live).
   * @param watchIntervalMs Poll interval in ms (default TIMING.AUDIO_CONTEXT_WATCH_MS).
   */
  constructor(
    private readonly getContext: () => AudioContext | null,
    private readonly shouldKeepAlive: () => boolean,
    private readonly watchIntervalMs: number = TIMING.AUDIO_CONTEXT_WATCH_MS,
  ) {}

  start(): void {
    if (this.isWatching) return;
    this.isWatching = true;

    this.intervalId = setInterval(
      () => this.checkAndResume(),
      this.watchIntervalMs,
    );

    // Also react immediately on statechange events
    const ctx = this.getContext();
    if (ctx) this.attachStateChange(ctx);

    // iOS/Android suspend the AudioContext when the screen locks or the app is backgrounded.
    // The poll interval (2 s) is too slow to recover immediately on screen-unlock. Listening
    // to visibilitychange lets us attempt a resume the moment the user unlocks their phone.
    document.addEventListener("visibilitychange", this.handleVisibilityChange);

    // Chrome Android can fully freeze a tab under memory pressure. When the tab
    // is unfrozen, the Page Lifecycle `resume` event fires on window — but
    // `visibilitychange` may NOT fire. Without this listener, bells stay silent
    // after Chrome Android unfreezes the tab (MOB-05).
    window.addEventListener("resume", this.handlePageResume);

    this.log.info("AudioContextWatcher started");
  }

  stop(): void {
    if (!this.isWatching) return;
    this.isWatching = false;

    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    const ctx = this.getContext();
    if (ctx) {
      try {
        ctx.removeEventListener("statechange", this.handleStateChange);
      } catch (_) {}
    }

    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    window.removeEventListener("resume", this.handlePageResume);

    this.log.info("AudioContextWatcher stopped");
  }

  private attachStateChange(ctx: AudioContext): void {
    try {
      ctx.addEventListener("statechange", this.handleStateChange);
    } catch (_) {}
  }

  private handleStateChange = (): void => {
    this.checkAndResume();
  };

  private handleVisibilityChange = (): void => {
    if (!document.hidden) {
      this.checkAndResume();
    }
  };

  private handlePageResume = (): void => {
    this.checkAndResume();
  };

  private checkAndResume(): void {
    if (!this.isWatching) return;
    const ctx = this.getContext();
    if (!ctx || ctx.state !== "suspended") return;
    if (!this.shouldKeepAlive()) return;

    this.log.info("Context suspended during retreat. Attempting resume...");
    // iOS suspends both AudioContext and speechSynthesis together. Resume both so
    // any in-flight TTS utterance that was paused by the audio session interruption
    // continues playing.
    if (typeof window !== "undefined" && window.speechSynthesis?.paused) {
      window.speechSynthesis.resume();
      this.log.info("speechSynthesis resumed by watcher");
    }
    ctx
      .resume()
      .then(() => {
        this.log.info("AudioContext resumed by watcher");
      })
      .catch((err: unknown) => {
        this.log.trace(
          "Resume failed (likely no user gesture): %s",
          String(err),
        );
      });
  }
}
